import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Upload, Banknote, Receipt, Package, CheckCircle2,
  LogOut, Loader2, RefreshCcw, User, CheckCheck,
  Check, ArrowRight, Sparkles, Clock,
} from "lucide-react";
import { formatDateAT } from "@/lib/format";

/**
 * Sprint 29.1 — Operational Workspace.
 *
 * Restaurant employees (staff / waiter / kitchen) see today's task
 * checklist ordered by real workflow. Completed cards flip green.
 * A progress bar and latest-activity feed reassure the employee
 * their work is saved. No financial figures are ever rendered.
 */

const TASK_META = {
  daily_revenue: {
    icon: Banknote,
    accent: "from-emerald-500/15 to-emerald-500/5 border-emerald-200",
    iconBg: "bg-emerald-100 text-emerald-700",
    label: { tr: "Günlük Ciro", de: "Tagesumsatz" },
    doneLabel: { tr: "Ciro Girildi", de: "Umsatz erfasst" },
    hint:  { tr: "Bugünün ciro rakamlarını gir",
             de: "Heutigen Tagesumsatz erfassen" },
  },
  upload_receipt: {
    icon: Upload,
    accent: "from-sky-500/15 to-sky-500/5 border-sky-200",
    iconBg: "bg-sky-100 text-sky-700",
    label: { tr: "Fiş / Fatura Yükle", de: "Beleg hochladen" },
    doneLabel: { tr: "Belge Yüklendi", de: "Beleg hochgeladen" },
    hint:  { tr: "Metro, Kröswang, vs. fişlerini yükle",
             de: "Metro, Kröswang usw. Belege hochladen" },
  },
  stock_entry: {
    icon: Package,
    accent: "from-indigo-500/15 to-indigo-500/5 border-indigo-200",
    iconBg: "bg-indigo-100 text-indigo-700",
    label: { tr: "Stok Girişi", de: "Bestandserfassung" },
    doneLabel: { tr: "Stok Güncellendi", de: "Bestand aktualisiert" },
    hint:  { tr: "Yeni gelen ürünleri veya sayımı gir",
             de: "Wareneingang oder Zählung erfassen" },
  },
  expense_entry: {
    icon: Receipt,
    accent: "from-amber-500/15 to-amber-500/5 border-amber-200",
    iconBg: "bg-amber-100 text-amber-700",
    label: { tr: "Gider / Fatura Ekle", de: "Ausgabe erfassen" },
    doneLabel: { tr: "Gider Kaydedildi", de: "Ausgabe erfasst" },
    hint:  { tr: "Elle bir gider veya fatura ekle",
             de: "Manuelle Ausgabe oder Rechnung" },
  },
  daily_closing: {
    icon: CheckCheck,
    accent: "from-rose-500/15 to-rose-500/5 border-rose-200",
    iconBg: "bg-rose-100 text-rose-700",
    label: { tr: "Gün Sonu Kapanış", de: "Tagesabschluss" },
    doneLabel: { tr: "Gün Kapatıldı", de: "Tag abgeschlossen" },
    hint:  { tr: "Bugünün kapanışını işaretle",
             de: "Heutigen Abschluss bestätigen" },
  },
};

const ROLE_LABEL = {
  staff:   { tr: "Personel",  de: "Mitarbeiter" },
  waiter:  { tr: "Servis",    de: "Service" },
  kitchen: { tr: "Mutfak",    de: "Küche" },
};

function timeShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  } catch { return ""; }
}


export default function StaffDashboard() {
  const { lang } = useI18n();
  const { user, restaurant, logout } = useAuth();
  const nav = useNavigate();
  const de = lang === "de";

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/staff-workspace");
      setPayload(data);
      setError(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
                : detail?.message ? detail.message
                : (de ? "Panel yüklenemedi" : "Dashboard konnte nicht geladen werden");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // load once on mount

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const roleLabel = useMemo(() => {
    const role = (payload?.role || user?.role || "").toLowerCase();
    return ROLE_LABEL[role]?.[lang] || role;
  }, [payload?.role, user?.role, lang]);

  const brand = restaurant?.brand_name || restaurant?.name || "";
  const tasks = payload?.tasks || [];
  const progress = payload?.progress || { completed: 0, total: 0, pct: 0 };
  const allDone = !!payload?.all_done;
  const activity = payload?.latest_activity || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 pb-8"
         data-testid="staff-dashboard">
      {/* Header */}
      <header className="px-4 pt-6 pb-4 md:px-8 md:pt-8">
        <div className="max-w-2xl mx-auto flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-muted-foreground font-semibold">
              <User className="w-3.5 h-3.5" />
              <span data-testid="staff-role-label">{roleLabel}</span>
              {brand && (
                <>
                  <span className="opacity-40">·</span>
                  <span className="truncate">{brand}</span>
                </>
              )}
            </div>
            <h1 className="mt-1 font-display font-bold text-2xl md:text-3xl tracking-[-0.02em]"
                data-testid="staff-greeting">
              {de
                ? `Hallo, ${user?.full_name?.split(" ")[0] || ""}`
                : `Merhaba, ${user?.full_name?.split(" ")[0] || ""}`}
            </h1>
            <p className="text-sm text-muted-foreground mt-1 font-mono" data-testid="staff-today">
              {payload?.today
                ? formatDateAT(new Date(payload.today).toISOString())
                : formatDateAT(new Date().toISOString())}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost" size="icon"
              onClick={load}
              className="h-9 w-9 rounded-lg"
              data-testid="staff-refresh-btn"
              title={de ? "Aktualisieren" : "Yenile"}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost" size="icon"
              onClick={doLogout}
              className="h-9 w-9 rounded-lg"
              data-testid="staff-logout-btn"
              title={de ? "Abmelden" : "Çıkış"}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="px-4 md:px-8">
        <div className="max-w-2xl mx-auto space-y-5">
          {error && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
                 data-testid="staff-error">
              {error}
            </div>
          )}

          {/* Progress card */}
          <ProgressCard
            progress={progress}
            allDone={allDone}
            lang={lang}
            loading={loading && !payload}
          />

          {/* Today's Tasks */}
          <div className="space-y-3" data-testid="staff-tasks-list">
            <div className="flex items-center justify-between px-1">
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
                {de ? "Heutige Aufgaben" : "Bugünün Görevleri"}
              </div>
              <div className="text-[11px] font-mono text-muted-foreground" data-testid="staff-progress-count">
                {progress.completed} / {progress.total} {de ? "erledigt" : "tamamlandı"}
              </div>
            </div>

            {loading && !payload && (
              <>
                {[0, 1, 2].map(i => (
                  <div key={i} className="h-20 rounded-2xl border bg-muted/30 animate-pulse" />
                ))}
              </>
            )}

            {tasks.map((task) => (
              <TaskCard key={task.key} task={task} lang={lang} onGo={() => nav(task.route)} />
            ))}

            {!loading && tasks.length === 0 && (
              <div className="rounded-xl border bg-muted/30 p-6 text-center text-sm text-muted-foreground"
                   data-testid="staff-no-tasks">
                {de
                  ? "Für deine Rolle sind keine Aufgaben freigeschaltet."
                  : "Rolüne uygun bir görev tanımlı değil."}
              </div>
            )}
          </div>

          {/* Latest Activity */}
          <ActivityFeed items={activity} lang={lang} />

          {/* Bottom note */}
          <p className="text-[11px] text-muted-foreground text-center px-4 pt-2 leading-relaxed">
            {de
              ? "Deine Rolle sieht keine Finanzzahlen. Bei Fragen wende dich an den Eigentümer."
              : "Rolün finansal rakamları göremez. Sorularını sahibiyle paylaş."}
          </p>
        </div>
      </div>
    </div>
  );
}


function ProgressCard({ progress, allDone, lang, loading }) {
  const de = lang === "de";
  if (loading) {
    return <div className="h-28 rounded-2xl border bg-muted/30 animate-pulse" />;
  }
  if (allDone) {
    return (
      <Card className="rounded-2xl border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100/40 shadow-elev-1"
            data-testid="staff-all-done-card">
        <CardContent className="p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow-elev-2">
            <Sparkles className="w-6 h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold text-lg tracking-tight text-emerald-800"
                 data-testid="staff-all-done-msg">
              {de ? "Heute alles erledigt!" : "Bugünün işi tamam!"}
            </div>
            <div className="text-sm text-emerald-700/80 mt-0.5">
              {de ? "Großartige Arbeit — genieß den Feierabend."
                  : "Harika iş — güzel bir gün geçirdin."}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="rounded-2xl border shadow-elev-1" data-testid="staff-progress-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
              {de ? "Heutiger Fortschritt" : "Bugünün İlerlemesi"}
            </div>
            <div className="font-mono text-3xl font-bold tracking-[-0.02em] leading-tight"
                 data-testid="staff-progress-value">
              {progress.completed}
              <span className="text-lg text-muted-foreground font-normal"> / {progress.total}</span>
            </div>
          </div>
          <div className="w-14 h-14 rounded-full flex items-center justify-center bg-primary/10 text-primary font-mono font-bold"
               data-testid="staff-progress-pct">
            {progress.pct}%
          </div>
        </div>
        <div className="relative h-2 rounded-full bg-muted overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${Math.max(0, Math.min(100, progress.pct))}%` }}
            data-testid="staff-progress-bar"
          />
        </div>
      </CardContent>
    </Card>
  );
}


function TaskCard({ task, lang, onGo }) {
  const meta = TASK_META[task.key];
  if (!meta) return null;
  const Icon = meta.icon;
  const done = !!task.completed;

  if (done) {
    return (
      <div
        data-testid={`staff-task-${task.key}`}
        data-completed="true"
        className="w-full rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 flex items-center gap-4"
      >
        <div className="w-12 h-12 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-elev-1">
          <Check className="w-6 h-6" strokeWidth={2.6} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-base leading-tight text-emerald-800">
            {meta.doneLabel[lang]}
          </div>
          <div className="text-xs text-emerald-700/70 mt-0.5 truncate flex items-center gap-1.5">
            {task.actor_name && (
              <>
                <User className="w-3 h-3" />
                <span data-testid={`staff-task-${task.key}-actor`}>{task.actor_name}</span>
                <span className="opacity-40">·</span>
              </>
            )}
            <Clock className="w-3 h-3" />
            <span className="font-mono">{timeShort(task.completed_at)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={onGo}
      data-testid={`staff-task-${task.key}`}
      data-completed="false"
      className={`w-full text-left rounded-2xl border p-4 flex items-center gap-4
                  bg-gradient-to-br ${meta.accent}
                  transition-all hover:shadow-elev-3 hover:-translate-y-0.5 active:scale-[0.99]`}
    >
      <div className={`w-12 h-12 rounded-xl ${meta.iconBg} flex items-center justify-center shrink-0`}>
        <Icon className="w-6 h-6" strokeWidth={2.2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-base leading-tight">
          {meta.label[lang]}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 truncate">
          {meta.hint[lang]}
        </div>
      </div>
      <div className="text-muted-foreground shrink-0">
        <ArrowRight className="w-5 h-5" />
      </div>
    </button>
  );
}


function ActivityFeed({ items, lang }) {
  const de = lang === "de";
  return (
    <Card className="rounded-2xl border shadow-elev-1" data-testid="staff-activity-feed">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
            {de ? "Letzte Aktivitäten" : "Son Etkinlikler"}
          </div>
          <div className="text-[10px] text-muted-foreground font-mono">
            {items.length}
          </div>
        </div>
        {items.length === 0 && (
          <div className="text-xs text-muted-foreground py-2" data-testid="staff-activity-empty">
            {de ? "Heute noch nichts eingereicht." : "Bugün henüz bir kayıt yok."}
          </div>
        )}
        {items.map((a, i) => (
          <div key={`${a.kind}-${a.at}-${i}`}
               data-testid={`staff-activity-${i}`}
               className="flex items-start gap-3 py-1.5">
            <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
              <Check className="w-3.5 h-3.5" strokeWidth={2.6} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate">
                {a.actor_name && (
                  <span className="font-semibold" data-testid={`staff-activity-${i}-actor`}>
                    {a.actor_name}
                  </span>
                )}
                {a.actor_name && <span className="text-muted-foreground"> · </span>}
                <span className="text-foreground">{a.title}</span>
              </div>
              <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                {timeShort(a.at)}
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
